import React from 'react';
import {
  Button,
  CounterBadge,
  mergeClasses,
  useRestoreFocusTarget,
} from '@fluentui/react-components';
import useStatHeaderStyles from './StatHeader.styles';

interface IHeaderIconButtonProps {
  icon: JSX.Element;
  /** Accessible name; also used as the tooltip text. */
  label: string;
  /** Renders the button in its pressed state while its panel is open. */
  checked?: boolean;
  /** When greater than zero, an unread counter is overlaid on the icon. */
  badgeCount?: number;
  onClick: () => void;
}

/**
 * Replaces Coherence's `HeaderButton`. Always rendered at every viewport width — the two
 * header actions plus the profile avatar fit comfortably at 320px, which is why the old
 * `Overflow` / `useOverflowMenu` machinery and its mobile "More" menu are gone.
 */
const HeaderIconButton = ({
  icon,
  label,
  checked = false,
  badgeCount = 0,
  onClick,
}: IHeaderIconButtonProps): JSX.Element => {
  const styles = useStatHeaderStyles();
  const restoreFocusTargetAttributes = useRestoreFocusTarget();

  const iconContent =
    badgeCount > 0 ? (
      <span className={styles.badgeAnchor}>
        {icon}
        <CounterBadge
          className={styles.badge}
          count={badgeCount}
          overflowCount={99}
          size="small"
          appearance="filled"
          color="danger"
          aria-hidden
        />
      </span>
    ) : (
      icon
    );

  return (
    <Button
      {...restoreFocusTargetAttributes}
      className={mergeClasses(styles.actionButton, checked && styles.actionButtonChecked)}
      appearance="subtle"
      icon={iconContent}
      aria-label={label}
      aria-pressed={checked}
      title={label}
      onClick={onClick}
    />
  );
};

export default HeaderIconButton;
